/**
 * Atlas PM · 鉴权 Store
 *
 * 职责：
 *   - 持有 token 与当前用户（内存态）
 *   - 与 localStorage 同步（atlaspm_token / atlaspm_user）
 *   - 提供权限判断（超管恒 true）
 *
 * ⭐ 其他视图统一通过本 store 判断登录态与权限，不要各自解析 localStorage。
 */

import { create } from 'zustand';
import { authApi, type CurrentUser } from '../api/auth';
import { STORAGE_KEYS, setTokenGetter, setUnauthorizedHandler } from '../api/client';

interface AuthState {
  token: string | null;
  user: CurrentUser | null;
  /** 登录请求进行中 */
  loading: boolean;

  /** 登录：成功后写 localStorage，并再拉一次 /auth/me 确认用户信息 */
  login(username: string, password: string): Promise<void>;
  /** 登出：清后端权限缓存（best-effort）+ 清本地态 */
  logout(): void;
  /** 从 localStorage 恢复登录态（应用启动时调一次） */
  restore(): void;
  /** 是否已登录 */
  isAuthenticated(): boolean;
  /** ⭐ 权限判断：超管恒 true */
  hasPermission(code: string): boolean;
  /** ⭐ 多个权限任一满足 */
  hasAnyPermission(codes: string[]): boolean;
}

function safeGet(key: string): string | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function safeSet(key: string, value: string): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}
function safeRemove(key: string): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

export const useAuthStore = create<AuthState>((set, get) => ({
  token: null,
  user: null,
  loading: false,

  async login(username: string, password: string): Promise<void> {
    set({ loading: true });
    try {
      const res = await authApi.login(username, password);
      safeSet(STORAGE_KEYS.token, res.accessToken);
      safeSet(STORAGE_KEYS.user, JSON.stringify(res.user));
      set({ token: res.accessToken, user: res.user });

      // 登录响应已带 user，这里再拉一次 /auth/me 确认权限最新（口径一致）
      try {
        const me = await authApi.me();
        safeSet(STORAGE_KEYS.user, JSON.stringify(me));
        set({ user: me });
      } catch {
        /* /me 失败不阻塞登录，沿用登录响应中的 user */
      }
    } finally {
      set({ loading: false });
    }
  },

  logout(): void {
    if (get().token) {
      // best-effort：后端失败也要清本地
      authApi.logout().catch(() => undefined);
    }
    safeRemove(STORAGE_KEYS.token);
    safeRemove(STORAGE_KEYS.user);
    set({ token: null, user: null });
  },

  restore(): void {
    const token = safeGet(STORAGE_KEYS.token);
    const rawUser = safeGet(STORAGE_KEYS.user);
    let user: CurrentUser | null = null;
    if (rawUser) {
      try {
        user = JSON.parse(rawUser) as CurrentUser;
      } catch {
        user = null;
      }
    }
    if (token && user) {
      set({ token, user });
    } else {
      // 数据不完整则视为未登录，清理脏数据
      safeRemove(STORAGE_KEYS.token);
      safeRemove(STORAGE_KEYS.user);
      set({ token: null, user: null });
    }
  },

  isAuthenticated(): boolean {
    return Boolean(get().token);
  },

  hasPermission(code: string): boolean {
    const user = get().user;
    if (!user) return false;
    if (user.isSuperAdmin) return true;
    return user.permissions.includes(code);
  },

  hasAnyPermission(codes: string[]): boolean {
    return codes.some((code) => get().hasPermission(code));
  },
}));

/* 与 API 客户端绑定（单向依赖：store → client，无循环） */

// token 优先取内存态，回退 localStorage
setTokenGetter(() => useAuthStore.getState().token ?? safeGet(STORAGE_KEYS.token));

// 401 时清空内存态（localStorage 由 client 负责清理）
setUnauthorizedHandler(() => useAuthStore.setState({ token: null, user: null }));
